import { Card, ErrorBanner, LoadingButton, useActionSlow } from '@zarya/ui';
import { Icon, type IconName } from '@zarya/theme';
import { useCanOperar } from '@app/auth/useCan';
import { dataHour } from '@app/format';
import { hasKey, t } from '@app/i18n';
import { useTestInfraGroup } from './Infra.api';
import type { GroupTestable, InfraCheck, InfraGroup } from './InfraModel';
import { checkLabel } from './InfraModel';
import s from './Infra.module.css';

const ICONE: Record<string, IconName> = {
  ok: 'check-circle',
  falha: 'close',
  indeterminado: 'question',
};

/**
 * Rótulo do status de uma verificação. **Função, não const**: uma const de
 * módulo é avaliada no import e congelaria o idioma da primeira renderização.
 * A CHAVE (`ok`/`falha`/`indeterminado`) é o valor que o executor devolve — ela
 * não muda; muda o que se mostra.
 */
function statusLabel(status: string): string {
  return hasKey(`infrastructure.test.status.${status}`)
    ? t(`infrastructure.test.status.${status}`)
    : status;
}

function Verificacao({ check }: { check: InfraCheck }) {
  return (
    <li className={[s.check, s[`check_${check.status}`] ?? ''].filter(Boolean).join(' ')}>
      <Icon name={ICONE[check.status] ?? 'question'} size="sm" aria-hidden="true" />
      <span className={s.checkTexto}>
        <b>{checkLabel(check)}</b>
        {check.detail ? <span className={s.checkDetalhe}>{check.detail}</span> : null}
      </span>
      <span className={s.checkStatus}>{statusLabel(check.status)}</span>
    </li>
  );
}

export interface InfraTestCardProps {
  grupo: InfraGroup;
  /** O site cujo executor recebe o teste (S-e (2)). Ausente = o padrao. */
  siteId?: string;
}

/**
 * Card **Conexão** de uma aba (FR-019): dispara `testInfraConfigGroup` e mostra o
 * resultado **por verificação** — ok · falha · indeterminado, cada uma com o
 * detalhe que o executor devolveu.
 *
 * Dependência fora do ar não é erro HTTP: o resultado ruim chega em `data`.
 */
export function InfraTestCard({ grupo, siteId }: InfraTestCardProps) {
  const canTest = useCanOperar('testInfraConfigGroup');
  const test = useTestInfraGroup(siteId);
  const wait = useActionSlow();
  const resultado = test.data;

  if (!grupo.testable) return null;

  return (
    <Card title={t('infrastructure.test.title')} subtitle={t('infrastructure.test.subtitle')}>
      <div className={s.conexaoLinha}>
        <p className={s.conexaoAjuda}>
          {resultado?.tested_at
            ? t('infrastructure.test.lastRun', { when: dataHour(resultado.tested_at) ?? '' })
            : t('infrastructure.test.never')}
        </p>
        {canTest ? (
          <LoadingButton
            variant="out"
            icon="broadcast"
            loading={test.isPending}
            loadingLabel={t('infrastructure.test.running')}
            // `mutateAsync` em vez de `mutate` só para haver promessa que o
            // indicador de tarefas consiga esperar. O `catch` vazio é obrigatório:
            // o erro já está em `testar.error`, que alimenta o banner logo abaixo.
            onClick={() =>
              wait
                .executar(() => test.mutateAsync(grupo.name as GroupTestable), {
                  titulo: t('infrastructure.test.taskTitle'),
                })
                .catch(() => {})
            }
          >
            {t('infrastructure.test.run')}
          </LoadingButton>
        ) : null}
      </div>

      {test.error ? (
        <ErrorBanner error={test.error} title={t('infrastructure.test.error')} />
      ) : null}

      {resultado ? (
        <ul className={s.checks} data-testid="resultado-conexao" data-status={resultado.status}>
          {resultado.checks.map((check) => (
            <Verificacao key={check.name} check={check} />
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
